import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Radio,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useJobsList } from '@/hooks/useJob';
import type { JobSummary } from '@/types';
import { cn } from '@/lib/utils';

type SortField = 'date' | 'score' | 'recommendation' | 'title';
type SortDir = 'asc' | 'desc';

const REC_ORDER: Record<string, number> = {
  urgent_review: 4,
  review_now: 3,
  monitor: 2,
  ignore: 1,
};

const REC_STYLES: Record<string, { label: string; className: string }> = {
  ignore: { label: 'Ignore', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  monitor: { label: 'Monitor', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  review_now: { label: 'Review Now', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  urgent_review: { label: 'Urgent Review', className: 'bg-red-100 text-red-800 border-red-200' },
};

function scoreColor(score: number): string {
  if (score >= 75) return 'text-red-600 font-bold';
  if (score >= 50) return 'text-amber-600 font-semibold';
  if (score >= 25) return 'text-yellow-600';
  return 'text-green-600';
}

function sortJobs(jobs: JobSummary[], field: SortField, dir: SortDir): JobSummary[] {
  return [...jobs].sort((a, b) => {
    let cmp = 0;
    if (field === 'date') {
      cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    } else if (field === 'score') {
      cmp = (a.newsworthinessScore ?? -1) - (b.newsworthinessScore ?? -1);
    } else if (field === 'recommendation') {
      cmp = (REC_ORDER[a.recommendation ?? ''] ?? 0) - (REC_ORDER[b.recommendation ?? ''] ?? 0);
    } else if (field === 'title') {
      cmp = (a.episodeTitle ?? '').localeCompare(b.episodeTitle ?? '');
    }
    return dir === 'desc' ? -cmp : cmp;
  });
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function SortHeader({
  label,
  field,
  currentField,
  currentDir,
  onClick,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onClick: (field: SortField) => void;
}) {
  const isActive = currentField === field;
  return (
    <button
      onClick={() => onClick(field)}
      className={cn(
        'flex items-center gap-1 text-xs font-medium uppercase tracking-wider hover:text-foreground transition-colors',
        isActive ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      {label}
      {isActive && (currentDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
    </button>
  );
}

// Portal-based tooltip — renders at document.body to escape all overflow containers
function Tooltip({ text, children }: { text: string | null | undefined; children: React.ReactNode }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const show = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX });
  }, []);

  if (!text) return <>{children}</>;

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={() => {
          timerRef.current = setTimeout(show, 400);
        }}
        onMouseLeave={() => {
          if (timerRef.current) clearTimeout(timerRef.current);
          setPos(null);
        }}
      >
        {children}
      </div>
      {pos &&
        createPortal(
          <div
            style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 9999 }}
            className="w-72 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md pointer-events-none"
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'complete')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700">
        <CheckCircle2 className="h-3 w-3" /> Done
      </span>
    );
  if (status === 'failed')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <AlertCircle className="h-3 w-3" /> Failed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-xs text-blue-600">
      <Loader2 className="h-3 w-3 animate-spin" /> Running
    </span>
  );
}

export function DashboardPage() {
  const { jobs, loading, error, hasNextPage, nextCursor, refetch, fetchPage } = useJobsList();
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Stack of cursors visited so we can go back: index 0 = first page (undefined cursor)
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const sortedJobs = useMemo(() => sortJobs(jobs, sortField, sortDir), [jobs, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const goNext = () => {
    if (!nextCursor) return;
    const newStack = [...cursorStack.slice(0, pageIndex + 1), nextCursor];
    setCursorStack(newStack);
    setPageIndex(pageIndex + 1);
    fetchPage(nextCursor);
  };

  const goPrev = () => {
    if (pageIndex === 0) return;
    const newIndex = pageIndex - 1;
    setPageIndex(newIndex);
    fetchPage(cursorStack[newIndex]);
  };

  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status !== 'complete' && j.status !== 'failed');
    if (!hasRunning) return;
    const interval = setInterval(refetch, 20000);
    return () => clearInterval(interval);
  }, [jobs, refetch]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 pb-12 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analysis Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {jobs.length} episode{jobs.length !== 1 ? 's' : ''} analyzed
          </p>
        </div>
        <Button asChild>
          <Link to="/">New Analysis</Link>
        </Button>
      </div>

      {jobs.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4 py-8">
              <Radio className="h-12 w-12 text-muted-foreground/40" />
              <div>
                <h2 className="text-lg font-semibold">No analyses yet</h2>
                <p className="text-sm text-muted-foreground mt-1">Submit a podcast URL to get started.</p>
              </div>
              <Button asChild>
                <Link to="/">
                  Analyze Episode
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border">
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 w-8">#</th>
                  <th className="text-left p-3">
                    <SortHeader
                      label="Episode"
                      field="title"
                      currentField={sortField}
                      currentDir={sortDir}
                      onClick={toggleSort}
                    />
                  </th>
                  <th className="text-left p-3 hidden lg:table-cell">Show</th>
                  <th className="text-center p-3 w-20">
                    <SortHeader label="Score" field="score" currentField={sortField} currentDir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="text-center p-3 w-32">
                    <SortHeader
                      label="Priority"
                      field="recommendation"
                      currentField={sortField}
                      currentDir={sortDir}
                      onClick={toggleSort}
                    />
                  </th>
                  <th className="text-left p-3 hidden xl:table-cell">Summary</th>
                  <th className="text-left p-3 w-36">
                    <SortHeader label="Date" field="date" currentField={sortField} currentDir={sortDir} onClick={toggleSort} />
                  </th>
                  <th className="text-center p-3 w-20">Status</th>
                  <th className="text-right p-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((job, idx) => {
                  const rec = job.recommendation ? REC_STYLES[job.recommendation] : null;
                  const isComplete = job.status === 'complete';
                  const isFailed = job.status === 'failed';

                  return (
                    <tr
                      key={job.jobId}
                      className={cn(
                        'border-b last:border-0 hover:bg-accent/30 transition-colors',
                        isFailed && 'bg-destructive/5',
                      )}
                    >
                      <td className="p-3 tabular-nums text-muted-foreground">{idx + 1}</td>
                      <td className="p-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[250px]">{job.episodeTitle || 'Untitled Episode'}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[250px] lg:hidden">{job.showTitle}</p>
                        </div>
                      </td>
                      <td className="p-3 hidden lg:table-cell">
                        <p className="text-muted-foreground truncate max-w-[180px]">{job.showTitle || '—'}</p>
                      </td>
                      <td className="p-3 text-center">
                        {job.newsworthinessScore != null ? (
                          <span className={cn('tabular-nums text-base', scoreColor(job.newsworthinessScore))}>
                            {job.newsworthinessScore}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {rec ? (
                          <span
                            className={cn(
                              'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold',
                              rec.className,
                            )}
                          >
                            {rec.label}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="p-3 hidden xl:table-cell max-w-[280px]">
                        <Tooltip text={job.whySummary || (isFailed ? job.error : null)}>
                          <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 cursor-default">
                            {job.whySummary || (isFailed ? job.error : '—')}
                          </p>
                        </Tooltip>
                      </td>
                      <td className="p-3 tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(job.createdAt)}
                      </td>
                      <td className="p-3 text-center">
                        <StatusBadge status={job.status} />
                      </td>
                      <td className="p-3 text-right">
                        {isComplete ? (
                          <Link
                            to={`/jobs/${job.jobId}/results`}
                            className={cn(
                              'inline-flex items-center justify-center rounded-md px-3 py-1.5',
                              'text-xs font-semibold text-primary-foreground bg-primary shadow-sm',
                              'hover:bg-primary/90 transition-colors',
                            )}
                          >
                            View
                          </Link>
                        ) : !isFailed ? (
                          <Link
                            to={`/jobs/${job.jobId}`}
                            className={cn(
                              'inline-flex items-center justify-center rounded-md border border-input',
                              'bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent transition-colors',
                            )}
                          >
                            Status
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination — always visible so users know where they are */}
          <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30">
            <button
              onClick={goPrev}
              disabled={pageIndex === 0}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                pageIndex === 0
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-foreground hover:bg-accent border border-input bg-background',
              )}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </button>
            <span className="text-xs text-muted-foreground">Page {pageIndex + 1}</span>
            <button
              onClick={goNext}
              disabled={!hasNextPage}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                !hasNextPage
                  ? 'text-muted-foreground/40 cursor-not-allowed'
                  : 'text-foreground hover:bg-accent border border-input bg-background',
              )}
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
